import locale from "@bonliva-crm/locale";
import { MultiCreatableSelect, TextField } from "@bonliva-crm/ui";
import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  makeStyles,
  Typography
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Controller, UseFormReturn } from "react-hook-form";
import { SMSActionFormValues } from "./action-form-values";
import { useEffect } from "react";
import { getSmsTextHint, smsHelperText } from "@components/sms/sms-text-hint";
import { SMS_MAX_GSM7_LENGTH } from "@bonliva-crm/shared";

type Props = {
  form: UseFormReturn<SMSActionFormValues, any, SMSActionFormValues>;
  setSmsTotalCost: (cost: string) => void;
  /** Typed-text limit from the API; below 306 when the unsubscribe link is appended. */
  maxLength: number;
};

export const SMSAction = ({
  form: { control, watch },
  setSmsTotalCost,
  maxLength
}: Props) => {
  const classes = useStyles();

  const recipients = watch("recipients");
  const message = watch("message");
  const messageHint = getSmsTextHint(message, maxLength);

  const smsCost = 0.39;
  const smsMaxChars = 150;
  const numOfChars = message?.length ?? 0;
  const numOfSms = Math.ceil(numOfChars / smsMaxChars);
  const threshold = Math.floor((numOfChars - 1) / smsMaxChars);
  const thresholdChars = threshold * smsMaxChars;

  const totalCost = smsCost * numOfSms * recipients.length;
  const totalCostFormatted = totalCost.toFixed(2).replace(".", ",");

  useEffect(() => {
    setSmsTotalCost(totalCostFormatted);
  }, [message]);

  return (
    <FormControl component="fieldset" className={classes.root}>
      <section>
        <Controller
          render={({ field }) => (
            <Box className={classes.textContainer}>
              <Typography variant="body1">{`${locale.To}:`}</Typography>

              <div style={{ minWidth: "200px" }}>
                <MultiCreatableSelect
                  options={[]}
                  placeholder=""
                  value={field.value.map((v) => ({
                    label: `${v.name}: ${v.phone}`,
                    value: v
                  }))}
                  isOptionSelected={(option) =>
                    field.value.some((v) => v.phone === option.value.phone)
                  }
                  styleProps={{
                    menu: { showBorder: true },
                    control: { showBorder: false }
                  }}
                  maxValuesToDisplay={{
                    amount: 4,
                    valueToTooltipFn: (values) =>
                      values.map((v) => `${v.value.name}: ${v.value.phone}`).join(", ")
                  }}
                  isClearable={false}
                  isDisabled={true}
                />
              </div>
            </Box>
          )}
          name="recipients"
          control={control}
        />
      </section>

      <Divider />

      <section style={{ marginTop: "16px" }}>
        {numOfSms > 1 && (
          <section style={{ marginBottom: "20px" }}>
            <Alert variant={"filled"} severity={"warning"}>
              <Typography variant={"body1"}>
                {locale.smsCostAlertText(thresholdChars, totalCostFormatted)}
              </Typography>
            </Alert>
          </section>
        )}

        <Controller
          render={({ field }) => (
            <Box className={classes.messageContainer}>
              <FormLabel>
                <Typography variant="body1">{`${locale.Message}:`}</Typography>
              </FormLabel>
              <TextField
                {...field}
                multiline={true}
                inputProps={{ rows: 6 }}
                error={!messageHint.valid}
                helperText={
                  maxLength < SMS_MAX_GSM7_LENGTH
                    ? `${smsHelperText(messageHint)}. ${locale.smsUnsubscribeLinkReserved(
                        SMS_MAX_GSM7_LENGTH - maxLength
                      )}`
                    : smsHelperText(messageHint)
                }
                style={{ width: 300 }}
              />
            </Box>
          )}
          name="message"
          control={control}
        />
      </section>
    </FormControl>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem"
  },
  textContainer: {
    display: "flex",
    flexDirection: "row",
    columnGap: "0.5rem",
    alignItems: "center"
  },
  messageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem"
  }
}));
